/*
 * fn: getRankOverTimeHelpers
 *
 * Gets helpers for Rank Over Time graphs
 *
 */
import { type ComponentProps } from 'react'
import { type Tooltip, type XAxis, type YAxis } from 'recharts'

import { type SetType } from '@/defs/set'
import { getSetIcon } from '@/utils/cardUtil'
import { formatDateFromDaysSinceEpoch, formatFullNumber, getDaysSinceEpoch } from '@/utils/formatUtil'

const YAXIS_STEPS = [
  [2000, 1000],
  [1000, 500],
  [500, 250],
  [200, 100],
  [100, 50],
  [50, 25],
  [30, 15],
  [20, 10],
  [10, 5],
  [5, 2],
] as const

const XAXIS_TICKCOUNT = 6

type Props = {
  indexEnd: number
  indexStart: number
  points: { daysSinceEpoch: number; rank: number }[]
  setsRecent?: SetType[]
}

export const getRankOverTimeHelpers = ({ indexEnd, indexStart, points, setsRecent }: Props) => {
  const pointsWindow = points.slice(indexStart, indexEnd + 1)

  const pointsX = pointsWindow.map((d) => d.daysSinceEpoch)
  const pointsY = pointsWindow.map((d) => d.rank)

  const setLines = [...(setsRecent || [])]
    .reverse()
    .map((s) => ({
      code: s.code,
      icon: getSetIcon(s.icon_svg_uri),
      x: s.released_at ? getDaysSinceEpoch(s.released_at) : 0,
    }))
    .filter((s) => !!s.code && !!s.icon && s.x > 0)

  const minX = Math.min(...pointsX, Number.MAX_SAFE_INTEGER)
  const maxX = Math.max(...pointsX, 1)
  const ticksX = (() => {
    if (pointsWindow.length <= XAXIS_TICKCOUNT) return pointsWindow.map((d) => d.daysSinceEpoch)
    const step = (pointsWindow.length - 1) / (XAXIS_TICKCOUNT - 1)
    return Array.from({ length: XAXIS_TICKCOUNT }, (_, i) => pointsWindow[Math.floor(i * step)]?.daysSinceEpoch || 0)
  })()

  const minInput = Math.min(...pointsY, Number.MAX_SAFE_INTEGER)
  const maxInput = Math.max(...pointsY, 1)
  const range = maxInput - minInput
  const stepSize = YAXIS_STEPS.find(([threshold]) => range > threshold)?.[1] ?? 1

  const maxY = Math.ceil((maxInput + 1) / stepSize) * stepSize
  const minY = Math.max(1, Math.floor((minInput - 1) / stepSize) * stepSize)
  const ticksY: number[] = []
  let currTick = Math.max(stepSize, Math.ceil(minInput / stepSize) * stepSize)
  while (currTick <= maxInput) {
    ticksY.push(currTick)
    currTick += stepSize
  }

  const xAxis: ComponentProps<typeof XAxis> = {
    dataKey: 'daysSinceEpoch',
    domain: [minX, maxX],
    niceTicks: 'none',
    tickFormatter: (value: number) => formatDateFromDaysSinceEpoch(value, 'MMM yyyy') || String(value),
    ticks: ticksX,
  }

  const yAxis: ComponentProps<typeof YAxis> = {
    domain: [minY, maxY],
    niceTicks: 'none',
    tickFormatter: (value: number) => `#${formatFullNumber(value)}`,
    ticks: ticksY,
  }

  const tooltip: ComponentProps<typeof Tooltip> = {
    formatter: (value) => [`#${formatFullNumber(Number(value))}`, 'Rank'],
    labelFormatter: (_, payload) => {
      const daysSinceEpoch = parseInt(payload[0]?.payload?.daysSinceEpoch) || 0
      return formatDateFromDaysSinceEpoch(daysSinceEpoch, 'MMM yyyy') || String(daysSinceEpoch)
    },
  }

  return { setLines, tooltip, xAxis, yAxis }
}
